// What Studio does: sign the curator in, read the Curator API into the store,
// and run golden sets as test runs. Pages call these. See Design > Exchange,
// "Curator API" and "Test runs". Studio changes a hub only through the Toolset flow's calls below.

import type { ActionId, CommitSha, CuratorReply, HubId, InteractionId, TestRunId, TestRunKind } from "../../../src/exchange/curator-api.ts";
import type { Auth } from "../../../ui/src/index.ts";
import { createApi, type Api, type Result } from "../api.ts";
import { unreadByHub, type WaitingInteraction } from "../domain/agent.ts";
import { GOLDEN_BID, goldenResult, TEST_RUN_GAP_MS } from "../domain/golden.ts";
import type { GoldenQuestion, HubRecord } from "../domain/types.ts";
import { getState, setState, type GoldenRun, type Load } from "./store.ts";

let api: Api;
let auth: Auth | null = null;
export const initAuth = (a: Auth | null) => { auth = a; };
export const initApi = (a?: Api) => { api = a ?? createApi(getState().config.apiBase, undefined, () => (auth ? auth.token() : null)); };

export const signIn = () => { void auth?.signIn(); };
export const signOut = () => { auth?.signOut(); };

/** The session couldn't be refreshed: forget the curator and what was read for them. */
export function sessionLost() {
  stopGoldenRun();
  sentReads.clear();
  setState({ user: null, hubs: null, events: {}, demand: {}, toolsets: {}, activations: {}, hubSecrets: {}, agent: {}, agentActions: {}, agentReplies: {}, waitingInteractions: null, golden: null, goldenRun: null });
}

const settle = <T,>(r: Result<T>): Load<T> => (r.ok ? { status: "ok", value: r.value, at: Date.now() } : { status: "failed", error: r.error });

export async function loadHubs(force = false) {
  if (!force && getState().hubs) return;
  // A read after a change keeps the page up: only the first read blanks it.
  if (getState().hubs?.status !== "ok") setState({ hubs: { status: "loading" } });
  const result = settle(await api.hubs());
  // A refresh that fails keeps what the page already shows rather than blanking it; only the first read can fail the page.
  if (result.status === "failed" && getState().hubs?.status === "ok") return;
  setState({ hubs: result });
}

export async function loadEvents(hub: HubId, force = false) {
  if (!force && getState().events[hub]) return;
  setState((s) => ({ events: { ...s.events, [hub]: { status: "loading" } } }));
  const result = settle(await api.events(hub));
  setState((s) => ({ events: { ...s.events, [hub]: result } }));
}

/** A hub's demand, by its Hub ID. */
export async function loadDemand(hub: HubId, days: number, force = false) {
  const key = `${hub}:${days}`;
  if (!force && getState().demand[key]) return;
  setState((s) => ({ demand: { ...s.demand, [key]: { status: "loading" } } }));
  const result = settle(await api.demand(hub, days));
  setState((s) => ({ demand: { ...s.demand, [key]: result } }));
}

/** A hub's newest toolsets, by its Hub ID. A read that follows a change keeps the old list up until the new one arrives. */
export async function loadToolsets(hub: HubId, force = false) {
  if (!force && getState().toolsets[hub]) return;
  if (getState().toolsets[hub]?.status !== "ok") setState((s) => ({ toolsets: { ...s.toolsets, [hub]: { status: "loading" } } }));
  const result = settle(await api.toolsets(hub));
  if (result.status === "failed" && getState().toolsets[hub]?.status === "ok") return;
  setState((s) => ({ toolsets: { ...s.toolsets, [hub]: result } }));
}

/** A hub's activation history, by its Hub ID, kept up the same way. */
export async function loadActivations(hub: HubId, force = false) {
  if (!force && getState().activations[hub]) return;
  if (getState().activations[hub]?.status !== "ok") setState((s) => ({ activations: { ...s.activations, [hub]: { status: "loading" } } }));
  const result = settle(await api.activations(hub));
  if (result.status === "failed" && getState().activations[hub]?.status === "ok") return;
  setState((s) => ({ activations: { ...s.activations, [hub]: result } }));
}

/** A hub's secrets' states, by its Hub ID, kept up as the toolsets are. */
export async function loadHubSecrets(hub: HubId, force = false) {
  if (!force && getState().hubSecrets[hub]) return;
  if (getState().hubSecrets[hub]?.status !== "ok") setState((s) => ({ hubSecrets: { ...s.hubSecrets, [hub]: { status: "loading" } } }));
  const result = settle(await api.secrets(hub));
  if (result.status === "failed" && getState().hubSecrets[hub]?.status === "ok") return;
  setState((s) => ({ hubSecrets: { ...s.hubSecrets, [hub]: result } }));
}

// Connecting or disconnecting the Hub token reads the hub's secrets again,
// whether it succeeded or was refused, as the Toolset flow's changes read
// what they may have changed. See Design > Hub agent, "The credential path".
const secretsChanged = async <T,>(hub: HubId, call: Promise<Result<T>>): Promise<Result<T>> => {
  const r = await call;
  await loadHubSecrets(hub, true);
  return r;
};
export const connectHubToken = (hub: HubId) => secretsChanged(hub, api.connectHubToken(hub));
export const disconnectHubToken = (hub: HubId) => secretsChanged(hub, api.disconnectHubToken(hub));

// The Toolset flow's changes. Each returns the Exchange's answer for the
// screen to say in words, and then reads what it may have changed, whether
// it succeeded or was refused: a refusal often means the screen was stale
// (a grant that lapsed, a toolset that changed), and its advice must point at
// what is on screen. The reads are the hubs and the hub's toolsets.
const changed = async <T,>(hub: HubId, call: Promise<Result<T>>): Promise<Result<T>> => {
  const r = await call;
  await Promise.all([loadHubs(true), loadToolsets(hub, true)]);
  return r;
};
export async function register(repo: string) {
  const r = await api.register(repo);
  if (r.ok) await loadHubs(true);
  return r;
}
export const pushToken = (hub: HubId) => api.pushToken(hub);
export const grant = (hub: HubId, commit: string) => changed(hub, api.grant(hub, commit));
export const stage = (hub: HubId, commit: "main" | CommitSha) => changed(hub, api.stage(hub, commit));
export const startRun = (hub: HubId, commit: CommitSha, kind: TestRunKind) => changed(hub, api.startRun(hub, commit, kind));
export const readRun = (hub: HubId, runId: TestRunId) => api.run(hub, runId);
// Only an activation changes the activation history, so only it reads that again.
export const activate = async (hub: HubId, commit: CommitSha) => {
  const r = await changed(hub, api.activate(hub, commit));
  await loadActivations(hub, true);
  return r;
};
// A request for a repair round changes no toolset; it reads them again so a refusal that the screen was stale, a toolset gone or a hub archived, points at what's on screen.
export const requestRepair = (hub: HubId, commit: CommitSha) => changed(hub, api.requestRepair(hub, commit));
export const listHub = (hub: HubId) => changed(hub, api.list(hub));
export const unlistHub = (hub: HubId, reason?: string) => changed(hub, api.unlist(hub, reason));
export const setAudience = (hub: HubId, kind: "everyone" | "org") => changed(hub, api.setAudience(hub, kind));

/** The hub agent's newest activity for a hub, by its Hub ID, kept up across a read again as the toolsets are. */
export async function loadAgent(hub: HubId, force = false) {
  if (!force && getState().agent[hub]) return;
  if (getState().agent[hub]?.status !== "ok") setState((s) => ({ agent: { ...s.agent, [hub]: { status: "loading" } } }));
  const result = settle(await api.agent(hub));
  if (result.status === "failed" && getState().agent[hub]?.status === "ok") return;
  setState((s) => ({ agent: { ...s.agent, [hub]: result } }));
}

export const agentActionKey = (hub: HubId, actionId: ActionId) => `${hub} ${actionId}`;
/** One agent action's transcript. A running action's transcript grows, so the page reads it again with `force`. */
export async function loadAgentAction(hub: HubId, actionId: ActionId, force = false) {
  const key = agentActionKey(hub, actionId);
  if (!force && getState().agentActions[key]) return;
  if (getState().agentActions[key]?.status !== "ok") setState((s) => ({ agentActions: { ...s.agentActions, [key]: { status: "loading" } } }));
  const result = settle(await api.agentAction(hub, actionId));
  if (result.status === "failed" && getState().agentActions[key]?.status === "ok") return;
  setState((s) => ({ agentActions: { ...s.agentActions, [key]: result } }));
}

/**
 * Sends a Curator reply, then reads the activity again whatever the Exchange
 * answered: a reply that was refused because the Interaction closed, or was
 * answered elsewhere, must show how it stands. The reply form sends only a
 * reply checkReply took, normalized; the Exchange checks it again. See
 * Design > Hub agent, "Pausing for the curator".
 */
export async function replyToInteraction(hub: HubId, interactionId: InteractionId, reply: CuratorReply): Promise<Result<"delivered" | "pending">> {
  const r = await api.reply(hub, interactionId, reply);
  // A reply the Exchange kept that the hub agent will take up later: the answered Interaction says so.
  if (r.ok && r.value.delivery === "pending") { const note = r.value.detail; setState((s) => ({ agentReplies: { ...s.agentReplies, [`${hub} ${interactionId}`]: note } })); }
  await Promise.all([loadAgent(hub, true), loadWaitingInteractions()]);
  return r.ok ? { ok: true, value: r.value.delivery } : r;
}

/**
 * What needs the curator across their hubs: the Interactions waiting on
 * them. Read on sign-in, on a timer and after a reply, so it always reads
 * again; a read that fails after one that worked keeps what the page shows,
 * as the hubs' do, and the next read tries again.
 */
export async function loadWaitingInteractions() {
  if (!getState().user) return;
  if (getState().waitingInteractions?.status !== "ok") setState({ waitingInteractions: { status: "loading" } });
  const result = settle(await api.waitingInteractions());
  // Signed out while the read was out: nothing read for that curator is kept.
  if (!getState().user) return;
  if (result.status === "failed" && getState().waitingInteractions?.status === "ok") return;
  setState({ waitingInteractions: result });
}

// The read marks sent this session, by curator, hub and Interaction, so an
// inform shown again before the next read clears it isn't sent again.
const sentReads = new Set<string>();

/**
 * Sends the curator's read marks on the informs "For you to read" shows on
 * one hub, once each this session: the dot clears on the next read. A send
 * that fails is forgotten, so showing them again sends it again. See
 * Design > UI, "Needs you".
 */
export async function markInteractionsRead(hub: HubId, interactionIds: ReadonlyArray<InteractionId>) {
  const user = getState().user;
  if (!user) return;
  const keys = new Map(interactionIds.map((id) => [`${user.id} ${hub} ${id}`, id]));
  const unsent = [...keys].filter(([key]) => !sentReads.has(key));
  if (unsent.length === 0) return;
  for (const [key] of unsent) sentReads.add(key);
  const r = await api.markRead(hub, unsent.map(([, id]) => id));
  if (!r.ok) for (const [key] of unsent) sentReads.delete(key);
}

/** Sends the read marks on every inform "For you to read" shows, one request per hub. */
export async function markShownRead(unread: ReadonlyArray<WaitingInteraction>) {
  await Promise.all([...unreadByHub(unread)].map(([hub, ids]) => markInteractionsRead(hub, ids)));
}

export async function loadGolden(force = false) {
  if (!force && getState().golden) return;
  setState({ golden: { status: "loading" } });
  setState({ golden: settle(await api.golden()) });
}

/** Reads everything for a hub again. */
export async function refresh(hub: HubId, days: number) {
  await Promise.all([loadHubs(true), loadEvents(hub, true), loadDemand(hub, days, true)]);
}

// Each run has a number; stopping or starting another makes the older one's
// remaining steps do nothing.
let runNumber = 0;
let lastTestRunAt = 0;
const updateRun = (change: Partial<GoldenRun>) => setState((s) => (s.goldenRun ? { goldenRun: { ...s.goldenRun, ...change } } : {}));

/** Asks a hub's golden questions of that hub alone, named as the Consumer API targets hubs, one at a time as test runs, paced under CONSUMER_LIMITER. */
export async function runGolden(hub: HubRecord, questions: ReadonlyArray<GoldenQuestion>, staged: boolean) {
  const n = ++runNumber;
  setState({ goldenRun: { hub, staged, total: questions.length, results: [], status: "running" } });
  for (const g of questions) {
    const wait = lastTestRunAt + TEST_RUN_GAP_MS - Date.now();
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    if (n !== runNumber) return;
    lastTestRunAt = Date.now();
    const r = await api.testRun(g.question, { hub: hub.name, bid: GOLDEN_BID, staged });
    if (n !== runNumber) return;
    if (!r.ok) {
      updateRun({ status: "failed", error: r.error });
      return;
    }
    const result = goldenResult(g, r.value);
    setState((s) => (s.goldenRun ? { goldenRun: { ...s.goldenRun, results: [...s.goldenRun.results, result] } } : {}));
  }
  updateRun({ status: "done" });
}

export function stopGoldenRun() {
  runNumber++;
  if (getState().goldenRun?.status === "running") updateRun({ status: "stopped" });
}
